import type {AgentQuestion} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import type {ContinuationSpec} from './continuationSpec';

/**
 * AskUserResult are the answers to the pending questions, index-aligned; an
 * empty string means unanswered.
 */
export type AskUserResult = {answers: string[]};

/**
 * askUserContinuation turns the user's structured answers into the next-turn
 * prompt.
 *
 * TODO: Revisit `composerPolicy` once the product settles how abandoning
 * questions should work. `allow-abandon` left the composer typable while the
 * queue stayed paused, so a send past pending questions dispatched the queue
 * head rather than the typed message.
 */
export const askUserContinuation: ContinuationSpec<AskUserResult> = {
  toolName: 'ask_user',
  composerPolicy: 'block',
  parseResult(value: unknown): AskUserResult {
    const answers = (value as {answers?: unknown} | null)?.answers;
    if (
      !Array.isArray(answers) ||
      !answers.every(answer => typeof answer === 'string')
    ) {
      // NOTE: This can change in the future if needed, but opt for a simpler
      // contract for the time being.
      throw new Error('ask_user result must carry string answers');
    }

    return {answers};
  },
  buildContinuation(payload, {answers}) {
    if (payload.kind !== 'questions') {
      throw new Error('ask_user continuation requires pending questions');
    }

    return {
      parts: [
        {
          type: 'text',
          text: formatAnswersPrompt(payload.questions, answers),
          status: 'complete',
        },
      ],
    };
  },
};

/**
 * formatAnswersPrompt renders the answers as a plain-text prompt the agent
 * reads as normal user input.
 */
function formatAnswersPrompt(
  questions: AgentQuestion[],
  answers: string[]
): string {
  return questions
    .map(
      (q, i) =>
        `Q${i + 1}: ${q.question}\nA${i + 1}: ${answers[i] || '(no answer)'}`
    )
    .join('\n\n');
}
